import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { JobApplicationStatusBadge } from "@/components/job/JobApplicationStatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext"; // Keep useAuth to check if logged in
import { JobApiResponse, useApiClient } from "@/lib/api";
import {
  getLatestJobApplicationStatus,
  isActiveJob,
  sortJobsByPriorityAndRecency,
} from "@/lib/job-status";
import { Award, Briefcase, Edit3, Eye, EyeOff, HelpCircle, Hourglass, Play, Search, ThumbsUp, Users } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

// Generation status constants
const GENERATION_STATUS = {
  NONE: "NONE",
  PENDING: "pending",
  RESEARCHING: "researching",
  GENERATING: "generating",
  READY: "ready",
  GRADED: "graded",
};

// Tooltip messages for different statuses
const STATUS_TOOLTIPS = {
  [GENERATION_STATUS.NONE]: "Click to generate practice interview questions for this job",
  [GENERATION_STATUS.PENDING]: "We're going to get started on prepping your job listing in just a second",
  [GENERATION_STATUS.RESEARCHING]: "We are currently researching your job and the industry to learn how to interview you!",
  [GENERATION_STATUS.GENERATING]: "We are generating interview questions based on your job listing",
  [GENERATION_STATUS.READY]: "Ready to go!",
  [GENERATION_STATUS.GRADED]: "Your interview has been completed and graded - view your results!",
};

// Display text for different statuses
const STATUS_DISPLAY_TEXT = {
  [GENERATION_STATUS.NONE]: "--",
  [GENERATION_STATUS.PENDING]: "STARTING",
  [GENERATION_STATUS.RESEARCHING]: "RESEARCHING",
  [GENERATION_STATUS.GENERATING]: "GENERATING",
  [GENERATION_STATUS.READY]: "READY",
  [GENERATION_STATUS.GRADED]: "GRADED",
};

const HIDE_INACTIVE_JOBS_COOKIE = "hide_inactive_jobs";

const getHideInactiveJobsPreference = (): boolean => {
  if (typeof document === "undefined") return true;
  const cookie = document.cookie
    .split(";")
    .map((entry) => entry.trim())
    .find((entry) => entry.startsWith(`${HIDE_INACTIVE_JOBS_COOKIE}=`));

  if (!cookie) return true;

  const rawValue = cookie.split("=")[1];
  return rawValue === "false" ? false : true;
};

const setHideInactiveJobsPreference = (hideInactive: boolean) => {
  if (typeof document === "undefined") return;
  document.cookie = `${HIDE_INACTIVE_JOBS_COOKIE}=${hideInactive}; path=/; SameSite=Lax`;
};

const getNextInterviewDate = (interviews: any[]): string => {
  if (!interviews || interviews.length === 0) {
    return '—';
  }

  const now = new Date();
  const upcomingInterviews = interviews
    .map(interview => {
      const interviewDateStr = interview.interview_date;
      if (!interviewDateStr) return null;
      // Parse ISO datetime string - new Date() handles ISO format correctly
      const interviewDate = new Date(interviewDateStr);
      return interviewDate;
    })
    .filter((date): date is Date => date instanceof Date && !isNaN(date.getTime()) && date > now)
    .sort((a, b) => a.getTime() - b.getTime());

  if (upcomingInterviews.length === 0) {
    return '—';
  }

  const nextInterviewDate = upcomingInterviews[0];

  const diffSeconds = (nextInterviewDate.getTime() - now.getTime()) / 1000;
  const diffMinutes = Math.round(diffSeconds / 60);
  const diffHours = Math.round(diffSeconds / 3600);
  const diffDays = Math.round(diffSeconds / 86400);

  if (diffMinutes < 60) {
    return `In ${diffMinutes} minute${diffMinutes === 1 ? '' : 's'}`;
  }
  if (diffHours < 24) {
    return `In ${diffHours} hour${diffHours === 1 ? '' : 's'}`;
  }
  if (diffDays === 1) {
    return "Tomorrow";
  }
  if (diffDays < 7) {
    return `In ${diffDays} days`;
  }
  if (diffDays < 14) {
    return "Next week";
  }

  return nextInterviewDate.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric'
  });
};

const Jobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<JobApiResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [hideInactiveJobs, setHideInactiveJobs] = useState<boolean>(() => getHideInactiveJobsPreference());
  const { api } = useApiClient();
  const { user } = useAuth();

  const fetchJobs = useCallback(async () => {
    setIsLoading(true);
    try {
      const jobsResponse = await api.getJobs();
      setJobs(jobsResponse);
      setError(null);
    } catch (err) {
      setError("Failed to fetch data. Please try again later.");
      toast.error("Failed to load job data.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (user) {
      fetchJobs();
    }
  }, [user, fetchJobs]);

  const dashboardStats = useMemo(() => {
    const allInterviews = jobs.flatMap(job => job.job_interviews || []);
    const now = new Date();
    
    const completed_interviews = allInterviews.filter(interview => {
        const interviewDate = new Date(interview.interview_date);
        return interviewDate < now;
    }).length;

    const pending_interviews = allInterviews.filter(interview => {
        const interviewDate = new Date(interview.interview_date);
        return interviewDate >= now;
    }).length;

    return {
      total_jobs: jobs.length,
      completed_interviews,
      pending_interviews,
    };
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const matchingJobs = jobs.filter(
      (job) =>
        job.title.toLowerCase().includes(query) ||
        job.company.toLowerCase().includes(query)
    );
    const visibleJobs = hideInactiveJobs
      ? matchingJobs.filter((job) => isActiveJob(job))
      : matchingJobs;
    return sortJobsByPriorityAndRecency(visibleJobs);
  }, [jobs, searchTerm, hideInactiveJobs]);

  const handleHideInactiveChange = (checked: boolean | "indeterminate") => {
    const nextValue = checked === true;
    setHideInactiveJobs(nextValue);
    setHideInactiveJobsPreference(nextValue);
  };

  // Function to get the appropriate badge variant and class based on job status
  const getStatusBadgeProps = (status: string) => {
    if (status === GENERATION_STATUS.READY) {
      return {
        variant: "default",
        className: "bg-green-100 text-green-800",
      };
    } else if (status === GENERATION_STATUS.GRADED) {
      return {
        variant: "default",
        className: "bg-purple-100 text-purple-800",
      };
    } else if ([GENERATION_STATUS.RESEARCHING, GENERATION_STATUS.GENERATING].includes(status)) {
      return {
        variant: "secondary",
        className: "bg-blue-100 text-blue-800",
      };
    } else if (status === GENERATION_STATUS.NONE) {
      return {
        variant: "outline",
        className: "border-dashed border-gray-300 text-gray-500",
      };
    } else {
      return {
        variant: "outline",
        className: "bg-gray-100 text-gray-800",
      };
    }
  };

  // Function to get the display text for a job status
  const getStatusDisplayText = (status: string) => {
    return STATUS_DISPLAY_TEXT[status] || status;
  };

  // Function to get the tooltip text for a job status
  const getStatusTooltip = (status: string) => {
    return STATUS_TOOLTIPS[status] || "";
  };

  return (
    <DashboardLayout>
      <div className="space-y-0 sm:space-y-6 -m-4 sm:m-0 p-0 sm:p-6 bg-gray-100 min-h-screen">
        <div className="hidden sm:grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Jobs</CardTitle>
              <Briefcase className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboardStats.total_jobs}</div>
              <p className="text-xs text-muted-foreground">All jobs you are tracking</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completed Interviews</CardTitle>
              <ThumbsUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboardStats.completed_interviews}</div>
              <p className="text-xs text-muted-foreground">Interviews you've crushed</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Interviews</CardTitle>
              <Hourglass className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboardStats.pending_interviews}</div>
              <p className="text-xs text-muted-foreground">Upcoming interviews on your schedule</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Job Hunt Advice</CardTitle>
              <HelpCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text font-italic">Tailored job hunting tips - coming soon!</div>
            </CardContent>
          </Card>
        </div>
        <Card className="bg-white shadow-none sm:shadow-lg rounded-none sm:rounded-lg mx-0">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <CardTitle className="text-xl font-semibold text-gray-800">Jobs</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                  <Input
                    placeholder="Search jobs..."
                    className="pl-8"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleHideInactiveChange(!hideInactiveJobs)}
                  className="inline-flex items-center gap-2 rounded-full border-blue-200 bg-blue-50/60 text-blue-900 hover:bg-blue-100/70 hover:text-blue-900 whitespace-nowrap"
                  title={hideInactiveJobs ? "Inactive jobs are hidden" : "Inactive jobs are visible"}
                  aria-pressed={hideInactiveJobs}
                >
                  {hideInactiveJobs ? (
                    <>
                      <EyeOff className="h-4 w-4" />
                      Hide inactive jobs
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4" />
                      Show inactive jobs
                    </>
                  )}
                </Button>
                {filteredJobs.length > 0 && (
                  <Button asChild>
                    <Link to="/jobs/new">Create New Job</Link>
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-pulse flex flex-col items-center">
                  <Briefcase className="h-12 w-12 text-gray-300 mb-4" />
                  <div className="h-4 w-36 bg-gray-200 rounded mb-3"></div>
                  <div className="h-3 w-24 bg-gray-200 rounded"></div>
                </div>
              </div>
            ) : filteredJobs.length === 0 ? (
              <div className="text-center py-12">
                {searchTerm ? (
                  <>
                    <p className="text-gray-500 mb-4">
                      No jobs found matching "{searchTerm}"
                    </p>
                    <Button variant="outline" onClick={() => setSearchTerm("")}>
                      Clear Search
                    </Button>
                  </>
                ) : (
                  <>
                    <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Jobs Added Yet</h3>
                    <p className="text-gray-500 mb-6">
                      Add your first job description to start generating interview questions
                    </p>
                    <Button asChild>
                      <Link to="/jobs/new">Add Your First Job</Link>
                    </Button>
                  </>
                )}
              </div>
            ) : (
              <>
                {/* Mobile view - Card layout */}
                <div className="block sm:hidden space-y-1">
                  {filteredJobs.map((job) => {
                    const badgeProps = getStatusBadgeProps(job.generation_status);
                    const isReady = job.generation_status === GENERATION_STATUS.READY;

                    return (
                      <div
                        key={job.id}
                        onClick={() => navigate(`/jobs/${job.id}`)}
                        className="bg-white border-b border-gray-200 p-4 cursor-pointer hover:bg-gray-50 transition-colors mx-0"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 truncate text-sm">{job.title}</h3>
                            <p className="text-xs text-gray-600 truncate">{job.company || 'N/A'}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 flex-shrink-0 ml-2"
                            title="Edit Job"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/jobs/${job.id}`);
                            }}
                          >
                            <Edit3 className="h-3 w-3" />
                          </Button>
                        </div>
                        
                        {/* Application Status (if exists) */}
                        {getLatestJobApplicationStatus(job) && (
                          <div className="mb-2">
                            <JobApplicationStatusBadge
                              status={getLatestJobApplicationStatus(job)}
                            />
                          </div>
                        )}

                        {/* Resume Analysis (if exists) */}
                        {job.analysis?.fit_rating && (
                          <div className="mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-600">Resume Fit:</span>
                              <Badge
                                variant={job.analysis.fit_rating.toLowerCase().includes('excellent') || job.analysis.fit_rating.toLowerCase().includes('strong') ? 'default' : 
                                        job.analysis.fit_rating.toLowerCase().includes('good') || job.analysis.fit_rating.toLowerCase().includes('moderate') ? 'secondary' : 
                                        'outline'}
                                className={`text-xs px-2 py-0.5 ${
                                  job.analysis.fit_rating.toLowerCase().includes('excellent') || job.analysis.fit_rating.toLowerCase().includes('strong') ? 'bg-green-100 text-green-800 hover:bg-green-100' : 
                                  job.analysis.fit_rating.toLowerCase().includes('good') || job.analysis.fit_rating.toLowerCase().includes('moderate') ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100' : 
                                  'bg-red-100 text-red-800 hover:bg-red-100'
                                }`}
                              >
                                {job.analysis.fit_rating}
                              </Badge>
                            </div>
                          </div>
                        )}

                        {(job.generation_status === GENERATION_STATUS.READY || job.generation_status === GENERATION_STATUS.GRADED) && (
                          <div className="flex-shrink-0">
                              {job.generation_status === GENERATION_STATUS.READY ? (
                                <Button
                                  variant="outline_primary"
                                  size="sm"
                                  asChild
                                  className="text-xs px-2 py-1 h-7"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Link to={`/jobs/${job.id}`}>
                                    <Play className="h-3 w-3 mr-1" /> Open
                                  </Link>
                                </Button>
                              ) : job.generation_status === GENERATION_STATUS.GRADED ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  asChild
                                  className="border-purple-300 text-purple-700 hover:bg-purple-50 text-xs px-2 py-1 h-7"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Link to={`/jobs/${job.id}`}>
                                    <Award className="h-3 w-3 mr-1" /> Open
                                  </Link>
                                </Button>
                              ) : job.generation_status === GENERATION_STATUS.NONE ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-xs px-2 py-1 h-7 border-blue-300 text-blue-700 hover:bg-blue-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleGenerateQuestions(job.id);
                                  }}
                                >
                                  <HelpCircle className="h-3 w-3 mr-1" /> Generate
                                </Button>
                              ) : (
                                <span className="text-xs text-gray-500 italic px-2">
                                  {STATUS_DISPLAY_TEXT[job.generation_status as keyof typeof STATUS_DISPLAY_TEXT] || job.generation_status}
                                </span>
                              )}
                            </div>
                        )}
                        
                        {(job.persons.length > 0 || job.job_interviews.length > 0 || getNextInterviewDate(job.job_interviews) !== '—') && (
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            {job.persons.length > 0 && (
                              <div className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                <span>{job.persons.length}</span>
                              </div>
                            )}
                            {job.job_interviews.length > 0 && (
                              <div className="flex items-center gap-1">
                                <Briefcase className="h-3 w-3" />
                                <span>{job.job_interviews.length}</span>
                              </div>
                            )}
                            {getNextInterviewDate(job.job_interviews) !== '—' && (
                              <div className="flex items-center gap-1">
                                <span>{getNextInterviewDate(job.job_interviews)}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Desktop view - Table layout */}
                <div className="hidden sm:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-center">Job Title</TableHead>
                        <TableHead className="text-center">Company</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="text-center">Contacts</TableHead>
                        <TableHead className="text-center">Interviews</TableHead>
                        <TableHead className="text-center">Next Interview</TableHead>
                        <TableHead className="text-center">Resume Analysis</TableHead>
                        <TableHead className="text-center"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredJobs.map((job) => {
                        const badgeProps = getStatusBadgeProps(job.generation_status);
                        const isReady = job.generation_status === GENERATION_STATUS.READY;

                        return (
                          <TableRow
                            key={job.id}
                            onClick={() => navigate(`/jobs/${job.id}`)}
                            className="cursor-pointer"
                          >
                            <TableCell className="font-medium">{job.title}</TableCell>
                            <TableCell>{job.company || 'N/A'}</TableCell>
                            <TableCell className="text-center">
                              {getLatestJobApplicationStatus(job) ? (
                                <JobApplicationStatusBadge
                                  status={getLatestJobApplicationStatus(job)}
                                />
                              ) : (
                                <Badge
                                  variant={badgeProps.variant as any}
                                  className={badgeProps.className}
                                  title={getStatusTooltip(job.generation_status)}
                                >
                                  {getStatusDisplayText(job.generation_status)}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-center">{job.persons.length === 0 ? '—' : job.persons.length}</TableCell>
                            <TableCell className="text-center">{job.job_interviews.length === 0 ? '—' : job.job_interviews.length}</TableCell>
                            <TableCell className="text-center">{getNextInterviewDate(job.job_interviews)}</TableCell>
                            <TableCell className="text-center">
                              {job.analysis?.fit_rating ? (
                                <Badge
                                  variant={job.analysis.fit_rating.toLowerCase().includes('excellent') || job.analysis.fit_rating.toLowerCase().includes('strong') ? 'default' : 
                                          job.analysis.fit_rating.toLowerCase().includes('good') || job.analysis.fit_rating.toLowerCase().includes('moderate') ? 'secondary' : 
                                          'outline'}
                                  className={job.analysis.fit_rating.toLowerCase().includes('excellent') || job.analysis.fit_rating.toLowerCase().includes('strong') ? 'bg-green-100 text-green-800 hover:bg-green-100' : 
                                            job.analysis.fit_rating.toLowerCase().includes('good') || job.analysis.fit_rating.toLowerCase().includes('moderate') ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100' : 
                                            'bg-red-100 text-red-800 hover:bg-red-100'}
                                >
                                  {job.analysis.fit_rating}
                                </Badge>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell className="text-right job-actions">
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Edit Job"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent row click while allowing navigation
                                  navigate(`/jobs/${job.id}`); // Navigate to job detail/edit page
                                }}
                              >
                                <Edit3 className="h-4 w-4" />
                                <span className="sr-only">Edit Job</span>
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Jobs;
